import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  FileDown,
  Loader2,
  Phone,
  MapPin,
  FileSignature,
  PenLine,
  Send,
  Copy,
  Check,
  CheckCircle2,
  Clock,
  XCircle,
  Ban,
  FileText,
  ChevronRight,
  ClipboardList,
  ReceiptText,
} from 'lucide-react';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import {
  useContract,
  useUpdateContract,
  useDeleteContract,
  useCreateContract,
  useProviderSignContract,
  useSendContract,
  contractStatusLabel,
  isContentLocked,
  type ContractStatus,
} from '@/hooks/useContracts';
import { GenerateReceiptButton } from '@/components/receipts/GenerateReceiptButton';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { SignaturePad } from '@/components/quotes/SignaturePad';
import { downloadQuotePDF, type PDFQuoteData } from '@/lib/pdfGenerator';
import { DuplicateDocumentButton } from '@/components/documents/DuplicateDocumentButton';
import { buildContractDuplicate } from '@/lib/documentDuplication';
import { hexToRgb } from '@/lib/pdfTemplates/shared';
import { maskDocument } from '@/lib/documentUtils';
import { getPublicContractUrl } from '@/lib/urls';
import { SendMessageDialog, type SendChannel } from '@/components/messages/SendMessageDialog';
import { useClientContact } from '@/hooks/useClientContact';
import { useWork } from '@/hooks/useWork';
import { WorkTrail, type WorkTrailAction } from '@/components/work/WorkTrail';
import { workDocumentCount, WORK_ACTION_LABELS } from '@/lib/work';
import { WorkGroupSection } from '@/components/work/WorkGroupSection';
import type { ReceiptRouteState } from '@/pages/ReceiptFormPage';
import { todayIsoDate } from '@/lib/financeStatus';

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const formatDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '';

export default function ContractDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, profile } = useAuth();
  const { activeCompany } = useCompany();
  const noBrandingFooter = useFeature('no_branding_footer');

  const { contract, isLoading } = useContract(id);
  // O trabalho a que este contrato pertence — a raiz é o orçamento de origem.
  const { work, roots: workRoots, refetch: refetchWork } = useWork(
    contract?.id
      ? { kind: 'contract', id: contract.id, quoteId: contract.quote_id ?? null }
      : null,
  );
  // `contracts` guarda o telefone do tomador, mas não o e-mail — vem do cadastro.
  const clientContact = useClientContact(contract?.client_id);
  const updateContract = useUpdateContract();
  const deleteContract = useDeleteContract();
  const createContract = useCreateContract();
  const providerSign = useProviderSignContract();
  const sendContract = useSendContract();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [signName, setSignName] = useState('');
  const [signData, setSignData] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  /** Canal escolhido para o envio; `null` = diálogo fechado. */
  const [sendChannel, setSendChannel] = useState<SendChannel | null>(null);
  // Ver a nota no `QuoteDetailPage`: a ação de recibo passou a ser uniforme na
  // trilha, então a tela é dona do modal em vez de usar o botão verde próprio.
  const { isOnline } = useNetworkStatus();

  if (!user) {
    return (
      <MainLayout>
        <Header onBack={() => navigate('/contratos')} />
        <p className="py-16 text-center text-sm text-muted-foreground">Faça login para continuar.</p>
      </MainLayout>
    );
  }

  if (!contract) {
    return (
      <MainLayout>
        <Header onBack={() => navigate('/contratos')} />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FileSignature className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">
              {isLoading ? 'Carregando…' : 'Contrato não encontrado.'}
            </p>
            <Button variant="outline" className="mt-4" onClick={() => navigate('/contratos')}>
              Voltar
            </Button>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  const st = (contract.status ?? 'draft') as ContractStatus;
  const isDraft = st === 'draft';
  const isPending = contract._offline_pending === true;
  const providerSigned = Boolean(contract.provider_signed_at);

  /**
   * O que ESTA tela sabe fazer com o contrato assinado: levar à execução (OS) e
   * ao comprovante (recibo). A trilha escolhe qual vira a ação primária e
   * recolhe a outra; num contrato avulso, sem trilha, as duas caem no card de
   * fallback abaixo. O rótulo é o mesmo nos dois caminhos (`WORK_ACTION_LABELS`).
   *
   * Declarado DEPOIS de `st`/`isPending` de propósito: é o que ele lê, e uma
   * `const` referenciada antes da declaração estoura em TDZ (#92).
   */
  const workActions: WorkTrailAction[] =
    st !== 'signed'
      ? []
      : [
          {
            stage: 'service_order',
            icon: ClipboardList,
            disabled: !isOnline || isPending,
            onClick: () => goToNewServiceOrder(),
          },
          {
            stage: 'payment',
            icon: ReceiptText,
            disabled: !isOnline || isPending,
            onClick: () =>
              navigate('/recibos/novo', {
                state: {
                  prefill: {
                    quote_id: contract.quote_id ?? null,
                    client_id: contract.client_id ?? null,
                    payer_name: contract.client_name ?? null,
                    payer_document: contract.client_document ?? null,
                    payer_address: contract.client_address ?? null,
                    amount: Number(contract.total ?? 0),
                    description: contract.title ?? null,
                  },
                } satisfies ReceiptRouteState,
              }),
          },
        ];
  const clientSigned = Boolean(contract.client_signed_at);
  const publicUrl = contract.public_token ? getPublicContractUrl(contract.public_token) : null;
  const linkExpired =
    contract.expires_at && new Date(contract.expires_at).getTime() < Date.now() && !clientSigned;

  const openProviderSign = () => {
    setSignData(null);
    setSignName(contract.provider_signer_name || (profile as any)?.full_name || activeCompany?.name || '');
    setSignOpen(true);
  };

  const confirmProviderSign = async () => {
    if (!signData) {
      toast.error('Desenhe sua assinatura.');
      return;
    }
    if (!signName.trim()) {
      toast.error('Informe o nome de quem assina.');
      return;
    }
    try {
      await providerSign.mutateAsync({ contract, signatureDataUrl: signData, signerName: signName });
      toast.success('Contrato assinado como prestador.');
      setSignOpen(false);
    } catch {
      // toast handled by mutation
    }
  };

  const handleSend = async () => {
    // Offline-created drafts can't be sent (no real id / token flow is online).
    if (isPending) {
      toast.error('Este contrato ainda não foi sincronizado. Conecte-se à internet e tente novamente.');
      return;
    }
    if (!contract.client_document?.trim()) {
      toast.error('Informe o CPF/CNPJ do cliente antes de enviar (é o desafio de identidade).');
      return;
    }
    try {
      const res = await sendContract.mutateAsync({ contract });
      toast.success('Link de assinatura gerado! Compartilhe com o cliente.');
      // Copy immediately for convenience.
      try {
        await navigator.clipboard.writeText(res.url);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2500);
      } catch {
        /* clipboard may be unavailable; the link is shown below */
      }
    } catch {
      // toast handled by mutation
    }
  };

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
      toast.success('Link copiado.');
    } catch {
      toast.error('Não foi possível copiar. Copie manualmente abaixo.');
    }
  };

  /**
   * O texto do envio deixou de ser hardcoded aqui: os dois canais passam pelo
   * seletor de modelo (categoria 'contract'), que renderiza as variáveis e só
   * então codifica a URL. A normalização do telefone virou o helper único
   * `normalizeWhatsAppPhone` — o `startsWith('55')` que estava aqui tratava o
   * DDD 55 (Santa Maria/RS) como se fosse DDI e abria a conversa errada.
   */
  const contractMessageContext = {
    cliente: contract?.client_name || '',
    titulo: contract?.title || 'de prestação de serviço',
    link: publicUrl || '',
    empresa: activeCompany?.name || '',
  };

  const handleCancel = async () => {
    try {
      await updateContract.mutateAsync({ id: contract.id, currentStatus: contract.status, updates: { status: 'cancelled' } });
      toast.success('Contrato cancelado.');
    } catch {
      // handled by mutation
    }
  };

  /**
   * Deriva uma ORDEM DE SERVIÇO deste contrato.
   *
   * Quando o contrato nasceu de um orçamento, o que interessa é o ORÇAMENTO —
   * ele traz itens e fotos, e a OS precisa deles. Sem orçamento de origem, só
   * os campos escalares do contrato (cliente/título) têm o que semear, e eles
   * viajam no state da navegação por serem um objeto.
   */
  const goToNewServiceOrder = () => {
    if (contract?.quote_id) {
      navigate(`/ordens-servico/nova?orcamento=${contract.quote_id}`);
      return;
    }
    navigate('/ordens-servico/nova', {
      state: {
        seed: {
          client_id: contract?.client_id ?? null,
          client_name: contract?.client_name ?? null,
          client_phone: contract?.client_phone ?? null,
          client_address: contract?.client_address ?? null,
          client_document: contract?.client_document ?? null,
          title: contract?.title ?? null,
        },
      },
    });
  };

  const handleDelete = async () => {
    try {
      await deleteContract.mutateAsync(contract.id);
      toast.success('Contrato excluído.');
      navigate('/contratos');
    } catch {
      // handled by mutation
    }
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      const company = activeCompany as any;
      const pdfData: PDFQuoteData = {
        documentType: 'contrato',
        client: {
          name: contract.client_name || 'Não informado',
          phone: contract.client_phone || undefined,
          address: contract.client_address || undefined,
          document: contract.client_document || undefined,
        },
        company: company
          ? {
              name: company.name || 'Minha Empresa',
              cnpj: company.cnpj_cpf || undefined,
              address: company.address || undefined,
              phone: company.phone || undefined,
              email: company.email || undefined,
              logoUrl: company.logo_url || undefined,
            }
          : undefined,
        // Calendário LOCAL: `created_at` é TIMESTAMP, e recortar o ISO em UTC
        // imprime o DIA SEGUINTE em tudo criado depois das 21h (#114/D21).
        quoteDate: contract.created_at
          ? todayIsoDate(new Date(contract.created_at))
          : todayIsoDate(),
        items: [],
        total: Number(contract.total ?? 0),
        isPremium: noBrandingFooter.allowed,
        logoWatermark: company?.logo_watermark === true,
        brandColorRGB: company ? hexToRgb(company.primary_color || '#7c3aed') : [124, 58, 237],
        sequenceId: contract.sequence_id ?? undefined,
        showSequenceId: contract.show_sequence_id ?? false,
        contractContent: contract.content,
        providerSignatureDataUrl: contract.provider_signature,
        clientSignatureDataUrl: contract.client_signature,
        providerSignerName: contract.provider_signer_name,
        clientSignerName: contract.client_signer_name,
        clientSignerDocument: contract.client_signer_document,
        providerSignedAt: contract.provider_signed_at,
        clientSignedAt: contract.client_signed_at,
        auditIp: contract.client_sign_ip,
        auditContentSha256: contract.signed_content_sha256,
      };
      const clientSlug = (contract.client_name || 'cliente').replace(/[^a-zA-Z0-9]/g, '_');
      await downloadQuotePDF(pdfData, `contrato-${clientSlug}-${Date.now()}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error('Não foi possível gerar o PDF.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <MainLayout>
      <Header
        onBack={() => navigate('/contratos')}
        action={
          <div className="flex items-center gap-1">
            {isDraft && (
              <Button variant="ghost" size="icon" onClick={() => navigate(`/contratos/${id}/editar`)}>
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        }
      />

      <div className="space-y-4">
        {/* Origem — liga de volta ao orçamento que gerou este contrato. */}
        {contract.quote_id && !String(contract.quote_id).startsWith('offline_') && (
          <button
            type="button"
            onClick={() => navigate(`/quotes/${contract.quote_id}`)}
            className="flex w-full items-center justify-between gap-2 rounded-md border border-dashed p-2.5 text-left text-sm transition hover:bg-muted"
          >
            <span className="flex items-center gap-2 text-muted-foreground">
              <FileText className="h-4 w-4" />
              Origem: orçamento
            </span>
            <ChevronRight className="h-4 w-4 opacity-60" />
          </button>
        )}

        {/* Overview */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-3">
              <CardTitle className="text-lg">{contract.title || contract.client_name || 'Contrato'}</CardTitle>
              <div className="flex shrink-0 items-center gap-1">
                {isPending && <Badge variant="outline">Offline</Badge>}
                <Badge className="border-0" variant="secondary">{contractStatusLabel(st)}</Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {contract.client_name && (
              <div>
                <p className="font-medium">{contract.client_name}</p>
                {contract.client_document && (
                  <p className="text-xs text-muted-foreground">Doc.: {maskDocument(contract.client_document)}</p>
                )}
                {contract.client_phone && (
                  <p className="flex items-center gap-1.5 text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" /> {contract.client_phone}
                  </p>
                )}
                {contract.client_address && (
                  <p className="flex items-center gap-1.5 text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" /> {contract.client_address}
                  </p>
                )}
              </div>
            )}
            <div className="flex items-center justify-between border-t pt-2 font-semibold">
              <span>Valor</span>
              <span>{formatCurrency(Number(contract.total ?? 0))}</span>
            </div>
          </CardContent>
        </Card>

        {/* Signatures state */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Assinaturas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <SignatureRow
              label="Prestador"
              signed={providerSigned}
              name={contract.provider_signer_name}
              when={contract.provider_signed_at}
            />
            <SignatureRow
              label="Cliente"
              signed={clientSigned}
              name={contract.client_signer_name}
              doc={contract.client_signer_document}
              when={contract.client_signed_at}
              ip={contract.client_sign_ip}
            />
            {st === 'signed' && (
              <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-xs font-medium">Contrato assinado pelas duas partes.</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trilha do trabalho — só quando há corrente. Um contrato AVULSO não
            alcança nada: `service_orders` não tem `contract_id`, e nem recibo nem
            NFS-e nem financeiro têm coluna de contrato. Nesse caso a trilha
            desenharia quatro etapas vazias e mentiria por omissão.

            O AGRUPAMENTO fica sempre neste mesmo slot — dentro do accordion
            quando há trilha, solto quando não há. Ele NÃO pertence ao card de
            ações abaixo: aquele card é gated em `signed` porque gerar OS ou
            recibo de um contrato não assinado é prematuro, e essa razão não vale
            para agrupar, que é só dizer a que trabalho o documento pertence. Com
            o agrupamento lá dentro, um contrato avulso em RASCUNHO não tinha
            como entrar num trabalho — e a tela de OS, que oferece a ação em
            qualquer status, ficava inconsistente com esta sem motivo. */}
        {workDocumentCount(work) > 1 ? (
          <WorkTrail
            work={work!}
            anchorId={contract.id}
            actions={workActions}
            onOpenDocument={(route) => navigate(route)}
          >
            <WorkGroupSection roots={workRoots} onChanged={refetchWork} />
          </WorkTrail>
        ) : (
          <WorkGroupSection roots={workRoots} onChanged={refetchWork} />
        )}

        {/* Contrato AVULSO não tem corrente (`service_orders` não tem
            `contract_id`), então a trilha não aparece e as ações continuam
            precisando de um lugar. Mesmo conteúdo, sem duplicar quando a trilha
            está presente. */}
        {st === 'signed' && workDocumentCount(work) <= 1 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Próximos passos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {workActions.map((action) => (
                <Button
                  key={action.stage}
                  variant="outline"
                  className="w-full justify-between"
                  disabled={action.disabled}
                  onClick={action.onClick}
                >
                  <span className="flex items-center">
                    <action.icon className="mr-2 h-4 w-4" />
                    {WORK_ACTION_LABELS[action.stage].start}
                  </span>
                  <ChevronRight className="h-4 w-4 opacity-60" />
                </Button>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Provider actions */}
        {st !== 'cancelled' && st !== 'signed' && (
          <div className="space-y-2">
            {!providerSigned && (
              <Button3D className="w-full" onClick={openProviderSign}>
                <PenLine className="mr-2 h-4 w-4" />
                Assinar como prestador
              </Button3D>
            )}
            <Button3D className="w-full" onClick={handleSend} disabled={sendContract.isPending}>
              {sendContract.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {publicUrl ? 'Reenviar / atualizar link' : 'Enviar para assinatura'}
            </Button3D>
            <p className="text-center text-xs text-muted-foreground">
              Enviar e assinar exigem conexão com a internet.
            </p>
          </div>
        )}

        {/* Public link / share */}
        {publicUrl && st !== 'cancelled' && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Link de assinatura do cliente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {clientSigned ? (
                <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
                  <CheckCircle2 className="h-4 w-4" /> Cliente já assinou por este link.
                </div>
              ) : linkExpired ? (
                <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400">
                  <Clock className="h-4 w-4" /> O link expirou. Use “Reenviar / atualizar link” para gerar um novo prazo.
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-4 w-4" /> Aguardando a assinatura do cliente.
                  {contract.expires_at && (
                    <span>Expira em {new Date(contract.expires_at).toLocaleDateString('pt-BR')}.</span>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Input readOnly value={publicUrl} className="text-xs" onFocus={(e) => e.currentTarget.select()} />
                <Button type="button" variant="outline" size="icon" onClick={copyLink} title="Copiar link">
                  {linkCopied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              {!clientSigned && (
                <div className="flex gap-2">
                  <Button3D
                    type="button"
                    color="whatsapp"
                    className="flex-1"
                    onClick={() => setSendChannel('whatsapp')}
                  >
                    <WhatsAppIcon size={18} className="mr-2" />
                    WhatsApp
                  </Button3D>
                  <Button3D
                    type="button"
                    color="gray"
                    className="flex-1"
                    onClick={() => setSendChannel('email')}
                  >
                    E-mail
                  </Button3D>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Contract body */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Texto do contrato</CardTitle>
              {!isDraft && (
                <span className="text-[10px] text-muted-foreground">bloqueado após envio</span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-xs leading-relaxed text-foreground/90">
              {contract.content || 'Sem conteúdo.'}
            </p>
          </CardContent>
        </Card>

        <Button3D className="w-full" onClick={generatePDF} disabled={generating}>
          {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
          Gerar PDF
        </Button3D>

        {/* Contrato-modelo reaproveitado para outro cliente/período. */}
        <DuplicateDocumentButton
          className="w-full"
          description="A cópia nasce como RASCUNHO editável, com número próprio. Ela não herda assinatura nenhuma, nem as datas, o IP e o hash do conteúdo assinado — isso é a trilha de auditoria de um ato jurídico, e copiada afirmaria que alguém assinou um documento que nunca viu. O link público de assinatura também é novo."
          onDuplicate={async () => {
            const created = await createContract.mutateAsync(buildContractDuplicate(contract as any));
            const newId = (created as { id?: string }).id ?? null;
            if (newId) {
              toast.success('Contrato duplicado como rascunho.');
              navigate(`/contratos/${newId}`);
            } else {
              toast.success('Cópia salva no aparelho e pronta para sincronizar.', { icon: '📴' });
              navigate('/contratos');
            }
            return newId;
          }}
        />

        {st !== 'cancelled' && st !== 'signed' && !isDraft && (
          <Button3D color="red" className="w-full" onClick={() => setConfirmCancel(true)}>
            <Ban className="mr-2 h-4 w-4" />
            Cancelar contrato
          </Button3D>
        )}
      </div>

      {/* Derivar OS deste contrato — do orçamento de origem se houver, senão dos
          dados do próprio contrato (cliente/título). */}

      {/* Provider signature dialog */}
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assinar como prestador</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Nome de quem assina</Label>
              <Input value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Seu nome" />
            </div>
            <SignaturePad label="Sua assinatura" onSignatureChange={setSignData} />
          </div>
          <DialogFooter>
            <Button3D color="gray" onClick={() => setSignOpen(false)} disabled={providerSign.isPending}>
              Cancelar
            </Button3D>
            <Button3D onClick={confirmProviderSign} disabled={providerSign.isPending}>
              {providerSign.isPending ? 'Assinando…' : 'Confirmar assinatura'}
            </Button3D>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirm */}
      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar contrato?</AlertDialogTitle>
            <AlertDialogDescription>
              O contrato ficará como cancelado e o link de assinatura deixará de funcionar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel}>Cancelar contrato</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir contrato?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O contrato e suas assinaturas serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Envio do contrato para assinatura (modelo + variáveis + revisão).
          `channels` recebe só o canal do botão clicado, preservando os dois
          pontos de entrada que já existiam. O telefone é o snapshot do contrato
          (dado do momento da emissão); o e-mail só existe no cadastro do
          cliente, então cai para `useClientContact`. */}
      <SendMessageDialog
        open={sendChannel !== null}
        onOpenChange={(open) => !open && setSendChannel(null)}
        category="contract"
        context={contractMessageContext}
        phone={contract?.client_phone || clientContact.phone}
        email={clientContact.email}
        emailSubject={`Contrato para assinatura — ${contract?.title || 'Prestação de serviço'}`}
        channels={sendChannel ? [sendChannel] : ['whatsapp']}
        title={sendChannel === 'email' ? 'Enviar contrato por e-mail' : 'Enviar contrato'}
      />
    </MainLayout>
  );
}

function SignatureRow({
  label,
  signed,
  name,
  doc,
  when,
  ip,
}: {
  label: string;
  signed: boolean;
  name?: string | null;
  doc?: string | null;
  when?: string | null;
  ip?: string | null;
}) {
  return (
    <div className="flex items-start gap-2">
      {signed ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
      ) : (
        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50" />
      )}
      <div className="min-w-0">
        <p className="font-medium">{label}</p>
        {signed ? (
          <p className="text-xs text-muted-foreground">
            {name || '—'}
            {doc ? ` · ${maskDocument(doc)}` : ''}
            {when ? ` · ${formatDateTime(when)}` : ''}
            {ip ? ` · IP ${ip}` : ''}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">Ainda não assinou.</p>
        )}
      </div>
    </div>
  );
}

function Header({ onBack, action }: { onBack: () => void; action?: React.ReactNode }) {
  return (
    <div className="mb-2 flex items-center gap-3">
      <Button variant="ghost" size="icon" onClick={onBack}>
        <ArrowLeft className="h-5 w-5" />
      </Button>
      <h1 className="flex-1 text-xl font-semibold">Contrato</h1>
      {action}
    </div>
  );
}
